import { ReactComponent as HeartIcon } from '@/assets/v2/icons/heart-full.svg';
import turboLora from '@/assets/v2/images/turbo-lora.png';
import fineTunedLlama from '@/assets/v2/images/fine-tuned-llama.png';
import checkr from '@/assets/v2/images/checkr.png';
import convirza from '@/assets/v2/images/convirza.png';
import marshMcLennan from '@/assets/v2/images/mm.png';
import texture from '@/assets/v2/images/texture.png';
import mistralAi from '@/assets/v2/logos/mistral-ai.svg';
import google from '@/assets/v2/logos/google.svg';
import meta from '@/assets/v2/logos/meta.svg';
import deepseek from '@/assets/v2/logos/deepseek.svg';
import gemma from '@/assets/v2/logos/gemma.png';
import heroImage from '@/assets/v2/images/hero-image.png';
import predibaseLogo from '@/assets/v2/logos/predibase.svg';
import rubrikLogo from '@/assets/v2/logos/rubrik.svg';

import fastestMultiLoraInference from '@/assets/v2/animations/fastest-multi-lora-inference.json';
import precisionFineTuning from '@/assets/v2/animations/precision-fine-tuning.json';
import reinforcementFineTuning from '@/assets/v2/animations/reinforcement-fine-tuning.json';
import ourCloud from '@/assets/v2/animations/our-cloud.json';
import fastestMultiLora from  '@/assets/v2/animations/fastest-multi-lora.json'
import effortlessGpuScaling from '@/assets/v2/animations/gpu-scaling.json'
import hundredsOfFineTunedModels from '@/assets/v2/animations/hundreds.json'
import builtForMissionCritical from '@/assets/v2/animations/mission-critical.json';

const homeData = {
  seo: {
    title:
      'Predibase: The Developers Platform for Fine-tuning and Serving LLMs',
    description:
      'Customize and serve open-source models for your use case that outperform GPT-4—all within your cloud or ours.',
  },
  hero: {
    // announcement: null,
    announcement: {
      icon: HeartIcon,
      title: 'Introducing Reinforcement Fine-Tuning.',
      description: {
        title: 'See it in Action',
        link: '/reinforcement-fine-tuning-playground',
      },
    },
    title:
      'Train The Best Models, <span class="text-orange">Serve at Maximum Speed.</span>',
    description:
      'Unmatched accuracy and speed with our end-to-end training and serving infra.<br />Now with reinforcement fine-tuning.',
    cta: [
      {
        title: 'Get Started',
        type: 'primary',
        href: '/free-trial',
      },
      {
        title: 'Book a Demo',
        type: 'secondary',
        href: '/request-a-demo',
      },
    ],
    video: 'https://player.vimeo.com/video/1067096756?h=5264022710&autoplay=0',
    image: heroImage,
  },
  bigAnnouncement: {
    title:
      'Rubrik Announces Intent to Acquire Predibase to Accelerate Agentic AI Adoption',
    description: [
      { type: 'logo', src: predibaseLogo, alt: 'Predibase', className: 'py-1' },
      { type: 'text', content: ' | ', className: 'text-2xl' },
      { type: 'logo', src: rubrikLogo, alt: 'Rubrik' },
    ],
    titleAndDescriptionClassName: 'max-w-[45rem]',
    link: {
      title: 'Read the blog',
      href: 'https://predibase.com/blog/predibase-will-be-joining-forces-with-rubrik',
    },
  },
  highlights: {
    preTitle: 'Adapt and Serve Open-Source LLMs.',
    title: 'Train with 1,000x Less Data. Serve 10x Faster.',
    items: [
      {
        title: 'The Fastest Multi-LoRA Inference',
        description:
          'Serve fine-tuned language models on autoscaling infrastructure with blazing-fast inference. Powered by LoRAX and Turbo LoRA.',
        link: {
          title: 'Try it for free',
          href: 'https://predibase.com/serving',
        },
        image: turboLora,
        lottie: fastestMultiLoraInference,
        imageDescription:
          'Turbo LoRA: 4x faster than other solutions<br /><br />',
      },
      {
        title: 'Precision Fine-Tuning with RFT',
        description:
          'Introducing the first reinforcement fine-tuning platform. Harness reward functions and 10 rows of labeled data to beat GPT-4.',
        link: {
          title: 'Try it for free',
          href: 'https://predibase.com/fine-tuning',
        },
        image: fineTunedLlama,
        lottie: precisionFineTuning,
        imageDescription:
          'View fine-tuned open-source SLMs consistently beat GPT-4.<br /><a href="https://predibase.com/fine-tuning-leaderboard" class="text-orange">View the leaderboard</a>',
      },
    ],
  },
  testimonials: {
    title: 'What Our Customers Say',
    items: [
      {
        company: 'convirza',
        logo: convirza,
        title: 'Predibase revolutionized our AI workflows.',
        quote:
          'At Convirza, our workload can be extremely variable, with spikes that require scaling up to double-digit A100 GPUs to maintain performance. The Predibase Inference Engine and LoRAX allow us to efficiently serve 60 adapters while consistently achieving an average response time of under two seconds. Predibase provides the reliability we need for these high-volume workloads. The thought of building and maintaining this infrastructure on our own is daunting—thankfully, with Predibase, we don’t have to.',
        link: '/blog/convirza-case-study',
        author: 'Giuseppe Romagnuolo',
        authorTitle: 'VP of AI, Convirza',
      },
      {
        company: 'checkr',
        logo: checkr,
        title: '5x cost reduction, faster than OpenAI.',
        quote:
          "By fine-tuning and serving Llama-3-8b on Predibase, we've improve accuracy, achieved lightning-fast inference and reduced costs by 5x compared to GPT-4. But most importantly, we’ve been able to build a better product for our customers, leading to more transparent and efficient hiring practices.",
        link: '/blog/how-checkr-streamlines-background-checks-with-fine-tuned-small-language',
        author: 'Vlad Bukhin',
        authorTitle: 'Staff ML Engineer, Checkr',
      },
      {
        company: 'marsh-mclennan',
        logo: marshMcLennan,
        title: 'Seamless enterprise-grade deployment.',
        quote:
          'With Predibase, I didn’t need separate infrastructure for every fine-tuned model, and training became incredibly cost-effective—tens of dollars, not hundreds of thousands. This combined unlocked a new wave of automation use cases that were previously uneconomical.',
        link: '/blog/how-marsh-mclennan-saved-over-1-million-hours-of-team-time-with-agentic-ai',
        author: 'Paul Beswick',
        authorTitle: 'Global CIO, Marsh McLennan',
      },
    ],
  },
  features: {
    title: 'The Ultimate Powerhouse for Serving Fine-Tuned SLMs',
    items: [
      {
        title: 'The Fastest Multi-LoRA Serving Available',
        description:
          'Unleash 4x Faster Throughput with Turbo LoRA. Serve models at ultra-fast speeds without sacrificing accuracy.',
        link: {
          title: 'Learn more',
          href: 'https://predibase.com/blog/turbo-lora',
        },
        lottie: fastestMultiLora,
      },
      {
        title: 'Hundreds of Fine-Tuned Models. One GPU.',
        description:
          'Run massive-scale inference with LoRAX-powered multi-LoRA serving. Stop wasting GPU capacity.',
        link: {
          title: 'Try it for free',
          href: 'https://github.com/predibase/lorax',
        },
        lottie: hundredsOfFineTunedModels,
      },
      {
        title: 'Effortless GPU Scaling. Peak Performance. No Surprises.',
        description:
          'Dynamically scale GPUs in real-time to meet any inference surge—zero slowdowns, zero wasted compute. Need guaranteed capacity? Reserve dedicated A100 & H100 GPUs for enterprise-grade reliability.',
        link: {
          title: 'Learn more',
          href: 'https://predibase.com/blog/predibase-inference-engine',
        },
        lottie: effortlessGpuScaling,
      },
      {
        title: 'Built for Mission-Critical Loads',
        description:
          '<ul class="orange-list"><li>Multi-Region High Availability</li><li>Logging and Metrics</li><li>Blue/Green Deployments</li><li>24/7 On-Call Rotation</li><li>SOC 2 Type II Certified</li></ul>',
        link: {
          title: 'Try it for free',
          href: 'https://predibase.com/free-trial',
        },
        lottie: builtForMissionCritical,
      },
      {
        title: 'Our Cloud or Yours',
        description:
          "Whether you're experimenting or running mission-critical AI, we’ve got you covered with flexible deployment options built for every stage of development.",
        link: {
          title: 'Learn more',
          href: 'https://predibase.com/predibase-virtual-private-cloud',
        },
        lottie: ourCloud,
      },
    ],
  },
  modelSupport: {
    title: 'Fine-Tune Any Base Model',
    description:
      'Seamlessly fine-tune from our expansive model library or deploy your own custom model with dedicated resources.',
    logos: [mistralAi, google, meta, deepseek, gemma],
    link: {
      title: 'Learn more',
      href: 'https://predibase.com/models',
    },
  },
  slmFeatures: {
    title: 'Train Specialized SLMs with or Without Training Data',
    subTitle: 'Reinforcement Fine-Tuning: Powering Continuous Iteration',
    description:
      'Train task-specific models with minimal data requirements. RFT builds upon GRPO to enable continuous learning through live reward functions. The result? Models that achieve exceptional accuracy even with limited training data.',
    link: {
      title: 'Schedule a Demo',
      href: 'https://go.predibase.com/rft-early-access',
    },
    lottie: reinforcementFineTuning,
    items: [
      {
        icon: HeartIcon,
        title: 'Start Without Labeled Data',
        description:
          'Fine-tune powerful models with just a few examples—no massive datasets required for rapid customization across any use case.',
      },
      {
        icon: HeartIcon,
        title: 'Models Improve Automatically',
        description:
          'RFT enables continuous learning with reward functions and improves model performance with each iteration.',
      },
      {
        icon: HeartIcon,
        title: 'Guide Live Training',
        description:
          'Adjust reward functions in real-time allowing immediate course correction.',
      },
    ],
  },
  cta: {
    title: 'Ready to efficiently fine-tune and serve your own LLM?',
    buttons: [
      {
        title: 'Get Started',
        href: '/free-trial',
      },
      {
        title: 'Get a demo',
        href: 'https://go.predibase.com/rft-early-access',
      },
    ],
    backgroundImage: texture,
  },
} as const;

export default homeData;
